import { Translation } from '../types';

export default  {
  // App
  appTitle: "OK Money - Life Plan Simulator",
  
  // Navigation
  navigation: {
    home: "Home",
    lifePlan: "Life Plan",
    riskSimulation: "Risk Simulation",
    contact: "Contact",
    openMenu: "Open Menu",
    lifePlanDescription: "Create a life plan to design your future life",
    riskSimulationDescription: "Run risk simulation to evaluate investment risks",
  },

  // Home
  home: {
    hero: {
      title: "Real Life Planning through Simulation",
      subtitle: "Optimize your life plan with simulations that consider uncertainty",
      download: "Download",
      docs: "Documentation"
    },
    features: {
      codeCompletion: {
        title: "Real-time Simulation",
        description: "Simulate market fluctuations in real-time and propose optimal money plans"
      },
      codebase: {
        title: "AI Analysis",
        description: "Analyze your investment portfolio using historical data and AI, and suggest improvements"
      },
      naturalLanguage: {
        title: "Investments that Enrich Your Life",
        description: "Grow your assets through investment while ensuring the security of funds needed for daily life"
      }
    },
  },
  
  // Company & Terms
  company: {
    title: "Company Information",
    basicInfo:"Basic Information",
    operatorName:"Operator Name",
    startDate:"Start Date",
    startDateValue:"2025-04-28",
    location:"Location",
    locationValue:"Yokohama, Japan",
    contact:"Contact",
    contactForm:"Contact Form",
    businessDescription:"Business Description",
    businessDescriptionContent:"We provide financial services to help your life better without stress about money.",
    developerProfile:"Developer Profile",
    developerProfileContent:"We are a team of developers who are passionate about creating financial services to help your life better without stress about money.",
    privacyPolicy:"Privacy Policy",
    privacyPolicyLink:"Privacy Policy is here",
    termsOfService:"Terms of Service",
    termsOfServiceContent:"We are a team of developers who are passionate about creating financial services to help your life better without stress about money.",
    contentEnd:"If you have any questions, please contact us.",
  },
  terms: {
    title: "Terms of Service",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    article1: {
      title: "Article 1 (Applicability)",
      content: "This agreement is for the conditions of use of the service (hereinafter referred to as \"this service\") provided by OKMoney (hereinafter referred to as \"this company\")."
    },
    article2: {
      title: "Article 2 (Registration)",
      content1: "1. To use this service, you need to register by agreeing to this agreement by the method specified by this company.",
      content2: "2. The company may reject the registration application if the applicant has violated this agreement or if the company deems the applicant's registration to be inappropriate.",
      listItem1: "Applicant who has violated this agreement",
      listItem2: "Other cases where the company deems the registration to be inappropriate"
    },
    article3: {
      title: "Article 3 (Prohibited Actions)",
      content: "Users must not engage in the following actions:",
      listItem1: "Actions that violate laws or public order",
      listItem2: "Actions related to criminal activities",
      listItem3: "Actions that may hinder the operation of the company's service",
      listItem4: "Actions that may annoy other users",
      listItem5: "Other actions that the company deems inappropriate"
    },
    article4: {
      title: "Article 4 (Suspension of Service)",
      content: "The company may suspend or interrupt the entire or part of the service without prior notice to the user if any of the following reasons are present:",
      listItem1: "When the company performs maintenance or updates to the computer system related to this service",
      listItem2: "Due to an earthquake, lightning, fire, power outage, or natural disaster, the provision of this service becomes difficult",
      listItem3: "Other cases where the company deems it difficult to provide the service"
    },
    article5: {
      title: "Article 5 (Disclaimer)",
      content: "The company is not responsible for any transactions, communications, or disputes between the user and other users or third parties."
    },
    article6: {
      title: "Article 6 (Changes to Service Content)",
      content: "The company may change the content of the service without prior notice to the user and may stop providing the service. The company is not responsible for any damages incurred by the user."
    },
    article7: {
      title: "Article 7 (Changes to Terms of Service)",
      content: "The company may change the terms of service without prior notice to the user."
    },
    article8: {
      title: "Article 8 (Notification or Contact)",
      content: "The company will notify or contact the user by the method specified by the company."
    },
    article9: {
      title: "Article 9 (Prohibition of Assignment of Rights and Obligations)",
      content: "The user may not assign or pledge the rights or obligations arising from the use contract or this agreement to a third party without the prior written consent of the company."
    },
    article10: {
      title: "Article 10 (Applicable Law and Jurisdiction)",
      content1: "1. The terms of this agreement will be interpreted in accordance with Japanese law.",
      content2: "2. In the event of a dispute regarding this service, the company's main office will be the exclusive jurisdiction."
    },
    privacy: {
      article1: {
        title: "Article 1 (Collection of Personal Information)",
        content: "The company may collect the following personal information when providing the service:",
        listItem1: "Name",
        listItem2: "Email Address",
        listItem3: "Address",
        listItem4: "Phone Number",
        listItem5: "Other information necessary for service provision"
      },
      article2: {
        title: "Article 2 (Purpose of Use of Personal Information)",
        content: "The collected personal information will be used for the following purposes:",
        listItem1: "Service provision and operation",
        listItem2: "Response to inquiries",
        listItem3: "Improvement and development of services",
        listItem4: "Notification of important information"
      },
      article3: {
        title: "Article 3 (Management of Personal Information)",
        content: "The company implements appropriate security measures to prevent the leakage, loss, or destruction of personal information."
      },
      article4: {
        title: "Article 4 (Third-Party Disclosure of Personal Information)",
        content: "The company will not provide personal information to third parties except in the following cases:",
        listItem1: "When the user consents",
        listItem2: "When required by law",
        listItem3: "When necessary to protect the life, body, or property of a human"
      },
      article5: {
        title: "Article 5 (Inquiry)",
        content: "Inquiries regarding the privacy policy can be made through the contact form."
      },
      article6: {
        title: "Article 6 (Changes to Privacy Policy)",
        content: "The company may change the privacy policy when necessary."
      }
    }
  },

  // Common
  common: {
    years: "years",
    perYear: "/year",
    perMonth: "/month",
    age: "Age",
    yes: "Yes",
    no: "No",
    
    none: "None",
    notEntered: "Not entered",
    add: "Add",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    reset: "Reset",
    month: "Month",
    select:"Select",
    clearSelection:"Clear Selection",
    dateTime:"Date & Time",
    actions:"Actions",
    currency: "Currency",
    currencyHelp: "Select the currency. The simulation will be performed in the selected currency.",
    currencies: {
      jpy: "Japanese Yen",
      usd: "US Dollar",
      eur: "Euro",
      gbp: "British Pound",
      aud: "Australian Dollar",
      cad: "Canadian Dollar",
      chf: "Swiss Franc",
      cny: "Chinese Yuan",
      hkd: "Hong Kong Dollar",
      sgd: "Singapore Dollar"
    },
    statistics:{
        title:"Statistics",
        mean:"Mean",
        median:"Median",
        min:"Min",
        max:"Max",
        mode:"Mode",
        totalInvestment:"Total Investment",
        assetDistribution:"Asset Distribution",
        targetAchieveRate:"Target Achieve Rate",
        probability:"Probability",
        medianFinalAsset:"Median Final Asset",
        finalAsset:"Final Asset",
    }
  },

  // Risk Simulation
  riskSimulation: {
    title: "Risk Simulation",
    simulationSettings: "Simulation Settings",
    initialInvestment:"Initial Investment",
    investmentAmount:"Investment Amount",
    investmentPeriod:"Investment Period",
    annualReturnRate:"Annual Return Rate",
    monthlyInvestment:"Monthly Investment",
    volatility:"Volatility",
    targetAmount:"Target Amount",
    runSimulation:"Run Simulation",
    running:"Running",
    simulationInstruction:"Please run the simulation first.",
    noSimulationRun:"No Simulation Run",
    simulationResults:"Simulation Results",
    currentResults:"Current Results",
    savedResults:"Saved Results",
    compare:"Compare",
    resultTitle: "Risk Simulation Results",
    simulationConditions:"Simulation Conditions",
    saveSimulationResult:"Save Simulation Result",
    saveSimulationDescription:"Please enter the name of the simulation result.",
    simulationName:"Simulation Name",
    simulationNamePlaceholder:"Enter the name of the simulation result.",
    saveSimulation:"Save Simulation",
    noSavedSimulations:"No Saved Simulations",
    noSavedSimulationsDescription:"No saved simulations found. Please run a simulation first.",
    createNewSimulation:"Create New Simulation",
    selectSimulationsToCompare:"Select Simulations to Compare",
    noSelectedSimulations:"No Selected Simulations",
    selectTwoOrMoreSimulations:"Please select two or more simulations to compare.",
    comparisonResults:"Comparison Results",
    finalAssetComparison:"Final Asset Comparison",
    finalAssetComparisonDescription:"Comparison of the final asset of the simulations.",
    successRateComparison:"Success Rate Comparison",
    successRateComparisonDescription:"Comparison of the success rate of the simulations.",
    simulationParameters:"Simulation Parameters",
    simulationParametersDescription:"Comparison of the simulation parameters of the simulations.",
    period: "Period",
    successRate: "Success Rate",
    backToSimulation: "Back to Simulation",
  },    
    
  // Life Plan
  lifePlan: {
    title: "Create Life Plan",
    resultTitle: "Life Plan Simulation Results",
    steps: {
      basicInfo: "Basic Information",
      incomeExpense: "Income & Expenses",
      assetsLiabilities: "Assets & Liabilities",
      lifeGoals: "Life Goals",
      simulationSettings: "Simulation Settings",
      confirmation: "Confirmation",
    },
    navigation: {
      prev: "Previous",
      next: "Next",
      startSimulation: "Start Simulation",
      confirmStart: "Do you want to start the simulation?",
      confirmStartTitle: "Confirm Simulation Start",
      cancel: "Cancel",
    },
    basicInfo: {
      title: "Basic Information",
      name: "Name",
      namePlaceholder: "Enter your name",
      age: "Age",
      gender: {
        title: "Gender",
        male: "Male",
        female: "Female",
        other: "Other",
      },
      familyStructure: {
        title: "Family Structure",
        hasSpouse: "Has Spouse",
        spouseAge: "Spouse's Age",
        children: "Children",
        help: "Enter your family structure",
        childrenHelp: "Add children and their ages",
        addChild: "Add Child",
        removeChild: "Remove Child",
        noChildren: "No children",
      },
      spouse: "Spouse",
      children: "Children"
    },
    incomeExpense: {
      title: "Income & Expenses",
      income: {
        title: "Income",
        annualIncome: "Annual Income",
        salaryIncreaseRate: "Salary Increase Rate",
        annualBonus: "Annual Bonus",
      },
      expense: {
        title: "Expenses",
        help: "Enter your monthly expenses",
        housingCost: "Housing Cost",
        housingCostHelp: "Enter your monthly housing expenses including rent or mortgage",
        utilities: "Utilities",
        food: "Food",
        communication: "Communication",
        transportation: "Transportation",
        entertainment: "Entertainment",
        investment: "Investment",
        stocks: "Stocks Investment",
        bonds: "Bonds Investment",
        mutualFunds: "Mutual Funds Investment",
        insurance: "Insurance Premium",
        other: "Other",
      },
      annualIncome: "Annual Income",
      salaryIncreaseRate: "Salary Increase Rate",
      annualBonus: "Annual Bonus",
      housingCost: "Housing Cost",
      utilities: "Utilities",
      food: "Food",
      communication: "Communication",
      transportation: "Transportation",
      entertainment: "Entertainment",
      other: "Other"
    },
    assetsLiabilities: {
      title: "Assets & Liabilities",
      assets: {
        title: "Assets",
        savings: "Savings",
        stocks: "Stocks",
        bonds: "Bonds",
        mutualFunds: "Mutual Funds",
        insurance: "Insurance",
        other: "Other",
        amount: "Amount",
        return: "Expected Return (%)",
        volatility: "Volatility (%)",
        investmentAssets: "Investment Assets",
      },
      liabilities: {
        title: "Liabilities",
        housingLoan: "Housing Loan",
        carLoan: "Car Loan",
        otherLoans: "Other Loans",
        addLoan: "Add Loan",
        removeLoan: "Remove Loan",
        name: "Loan Name",
        amount: "Loan Amount",
        period: "Repayment Period (years)",
        interestRate: "Interest Rate (%)",
        noLoans: "No Loans",
      },
      savings: "Savings",
      stocks: "Stocks",
      bonds: "Bonds",
      mutualFunds: "Mutual Funds",
      insurance: "Insurance",
      otherInvestments: "Other Investments",
      housingLoanBalance: "Housing Loan Balance",
      housingLoanPeriod: "Loan Period",
      housingLoanInterest: "Interest Rate",
      noLoans: "No Loans",
      otherLoans: "Other Loans"
    },
    lifeGoals: {
      title: "Life Goals",
      housingPurchase: "Housing Purchase",
      educationFunds: "Education Funds",
      child: "Child",
      otherGoals: "Other Goals",
      retirementAge: "Retirement Age",
      retirementMonthlyExpense: "Monthly Expenses After Retirement",
      notPlanned: "Not Planned",
      housing: {
        title: "Housing Goals",
        planned: "Planned Housing",
        year: "Planned Year",
        amount: "Planned Amount",
        purchaseYear: "Purchase Year",
        purchaseAmount: "Purchase Amount",
      },
      education: {
        title: "Education Goals",
        subtitle: "Education Fund Planning",
        add: "Add Education Fund",
        remove: "Remove Education Fund",
        name: "Education Fund Name",
        namePlaceholder: "Enter education fund name",
        childAge: "Child's Age",
        age: "Required Age",
        amount: "Required Amount",
        requiredAmount: "Required Amount",
        noData: "No education funds added",
      },
      other: {
        title: "Other Goals",
        subtitle: "Other Life Goals",
        add: "Add Goal",
        remove: "Remove Goal",
        name: "Goal Name",
        year: "Target Year",
        amount: "Required Amount",
        noData: "No goals added",
        namePlaceholder: "Enter other goal name",
        plannedAge: "Planned Age",
        requiredAmount: "Required Amount"
      },
      retirement: {
        title: "Retirement Planning",
        age: "Planned Retirement Age",
        monthlyExpense: "Monthly Expenses After Retirement",
      }
    },
    simulationSettings: {
      title: "Simulation Settings",
      inflationRate: "Inflation Rate (%)",
      investmentReturn: "Investment Return (%)",
      simulationPeriod: "Simulation Period (years)",
      currency: "Currency",
      currencyHelp: "Select the currency. The simulation will be performed in the selected currency.",
      inflationRateHelp: "Simulation considering future inflation rate."
    },
    runningSimulation:"Running Simulation",
    result: {
      title: "Life Plan Simulation Results",
      settings: "Life Plan Settings",
      planSettings: "Plan Settings",
      actions: {
        print: "Print",
        downloadCSV: "Download CSV",
        modifyConditions: "Modify Conditions",
        backToInput: "Back to Input",
      },
      tabs: {
        charts: "Charts",
        details: "Details",
        analysis: "Analysis & Recommendations",
      },
      charts: {
        assetIncomeExpense: "Asset & Income/Expense Trends",
        agePoint: "At age {age}",
        spouse: "Spouse: {age} years",
        children: "Children",
        age: "{age} years",
        assets: "Assets",
        liabilities: "Liabilities",
        netWorth: "Net Worth",
        amount: "Amount",
        noData: "No data available",
        assetTrend: "Asset Trends",
        incomeExpenseTrend: "Income/Expense Trends",
        income: "Income",
        expense: "Expense",
        savings: "Savings",
      },
      details: {
        yearlyData: "Yearly Data Details",
      },
      analysis: {
        risk: "Risk Analysis",
        recommendations: "Improvement Recommendations",
      },
      table: {
        search: "Search...",
        age: "Age",
        familyAge: "Family Age",
        income: "Income",
        expense: "Expense",
        savings: "Savings",
        assets: "Assets",
        lifeEvents: "Life Events",
        ageValue: "{age} years",
        spouse: "Spouse: {age} years",
        children: "Children",
        amount: "10,000",
        noData: "No data available",
      },
      timeline: {
        age: "Age",
        event: "Event",
        retirement: "Retirement",
        ageValue: "{age} years",
        agePoint: "At age {age}",
        spouse: "Spouse: {age} years",
        children: "Children",
        neededAmount: "Needed: {amount}",
        assets: "Assets: {amount}",
        education: "{childName}'s {fundName}",
        child: "Child",
        educationFund: "Education Fund"
      },
      summary: {
        title: "Summary",
        monthlyIncome: "Monthly Income",
        monthlyExpense: "Monthly Expense",
        monthlyInvestment: "Monthly Investment",
        monthlySavings: "Monthly Savings",
        totalAssets: "Total Assets",
        totalLiabilities: "Total Liabilities",
        netWorth: "Net Worth",
        finalAssets: "Final Assets",
        peakAssets: "Peak Assets",
        retirementAssets: "Retirement Assets",
        savingsRate: "Savings Rate",
        savingsRateDesc: "Savings as a percentage of income",
        retirementBalance: "Post-Retirement Balance",
        balance: "Balance",
        riskAssessment: "Risk Assessment",
        riskLow: "Low Risk",
        riskMedium: "Medium Risk",
        riskHigh: "High Risk",
        negativeAssets: "There will be periods with negative assets in the future.",
        positiveAssets: "Assets are expected to remain positive throughout.",
        needImprovement: "Income/expenses need improvement.",
        unit: "10,000",
        atAge: "At age {age}",
      },
      chart: {
        title: "Asset Trend Preview",
        noData: "Please enter data",
        assets: "Assets",
        income: "Income",
        expense: "Expense",
      },
    },
    confirmation: {
      title: "Confirmation"
    },
    validation: {
      error: "There is an error in the input."
    },
  },
  login: "Login",
  register: "Register",
  loginDescription: "Don't have an account? Please register.",
  registerDescription: "Create an account to start your life plan simulation.",
  email: "Email",
  password: "Password",
  passwordRequirements: "Password must be at least 8 characters long and include both letters and numbers.",
  loggingIn: "Logging in...",
  registering: "Registering...",
  loginFailed: "Login failed.",
  registrationSuccess: "Registration completed.",
  registrationFailed: "Registration failed.",
  myPage: "My Page",
  logout: "Logout",
  userProfile: "User Profile",
  userProfileDescription: "Manage your profile information",
  savedSimulations: "Saved Simulations",
  savedSimulationsDescription: "List of your saved simulation results",
  settings: "Settings",
  accountSettings: "Account Settings",
  accountSettingsDescription: "Manage your account settings",
  changePassword: "Change Password",
  changePasswordDescription: "Change your password",
} as Translation; 